import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { motion } from 'motion/react'
import './Navbar.css'
import { FaArrowRight, FaTimes } from 'react-icons/fa'
import { Menu } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { ROLES } from '@/config/roles'
import { trackBuyingJourney } from '@/services/customerJourneyService'
import {
  JOURNEY_FUNNEL_STAGES,
  JOURNEY_FUNNEL_EVENTS
} from '@/constants/buyingJourney'

const MotionLink = motion.create(Link)
const Navbar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { currentUser, userRole, logOut } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const [deviceType, setDeviceType] = useState('desktop')
  const [isVisible, setIsVisible] = useState(true)
  const [showTooltip, setShowTooltip] = useState(false)
  const lastScrollY = useRef(0)
  const longPressTimer = useRef(null)
  const longPressCompleted = useRef(false)
  const LONG_PRESS_DURATION = 800
  const SCROLL_THRESHOLD = 100
  const SCROLL_UP_THRESHOLD = 5
  const NAVBAR_HIDE_Y = -150

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }
  // set screen size and device type
  useEffect(() => {
    const checkDeviceType = () => {
      const width = window.innerWidth
      if (width >= 1024) {
        setDeviceType('desktop')
      } else if (width >= 768 && width <= 1023) {
        setDeviceType('tablet')
      } else if (width <= 767) {
        setDeviceType('mobile')
      }
    }

    checkDeviceType()
    window.addEventListener('resize', checkDeviceType)
    return () => {
      window.removeEventListener('resize', checkDeviceType)
    }
  }, [])

  useEffect(() => {
    const controlNavbar = () => {
      const currentScrollY = window.scrollY
      const prevScrollY = lastScrollY.current
      const scrollingDown = currentScrollY > prevScrollY
      const scrollingUp = prevScrollY - currentScrollY > SCROLL_UP_THRESHOLD

      if (scrollingDown && currentScrollY > SCROLL_THRESHOLD) {
        setIsVisible(false)
        setMenuOpen(false)
      } else if (scrollingUp || currentScrollY <= SCROLL_THRESHOLD) {
        setIsVisible(true)
      }

      lastScrollY.current = currentScrollY
    }

    window.addEventListener('scroll', controlNavbar, { passive: true })
    return () => window.removeEventListener('scroll', controlNavbar)
  }, [])

  useEffect(() => {
    if (!menuOpen) return
    const handleClickOutside = (event) => {
      const navbar = document.querySelector('.digipalz-landing-navbar__content')
      if (navbar && !navbar.contains(event.target)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [menuOpen])

  const handleRegisterClick = () => {
    trackBuyingJourney({
      stage: JOURNEY_FUNNEL_STAGES.A_LANDING,
      eventName: JOURNEY_FUNNEL_EVENTS.CTA_CLICK,
      cta_id: 'navbar_free_trial'
    })
  }

  const handleHowItWorksClick = (e) => {
    e.preventDefault()
    setMenuOpen(false)
    if (location.pathname === '/') {
      const el = document.getElementById('how-it-works')
      if (el) el.scrollIntoView({ behavior: 'smooth' })
      return
    }
    navigate('/', { state: { scrollToSection: 'how-it-works' } })
  }

  const getDashboardPath = () => {
    if (!currentUser || !userRole) return '/dashboard'

    switch (userRole) {
      case ROLES.ADMIN:
        return '/dashboard/admin'
      case ROLES.TEACHER:
        return '/dashboard/teacher/classroom'
      case ROLES.STUDENT:
        return '/dashboard/student/missions'
      case ROLES.CHILD:
        return '/dashboard/child/missions'
      case ROLES.PARENT:
        if (!currentUser?.hasSubscription) {
          return '/onboarding/parent/child-account-type'
        } else {
          return '/dashboard/parent/profiles'
        }

      case ROLES.SCHOOL_ADMIN:
        if (!currentUser?.hasSubscription) {
          return '/school-plan-options'
        } else {
          return '/dashboard/educator'
        }

      default:
        return '/dashboard'
    }
  }

  const handleDashboardAction = () => {
    setMenuOpen(false)
  }

  const handleDashboardLinkClick = (e) => {
    if (longPressCompleted.current) {
      e?.preventDefault()
      longPressCompleted.current = false
      return
    }
    handleDashboardAction()
  }

  const handleLongPressStart = (e) => {
    longPressCompleted.current = false

    longPressTimer.current = setTimeout(async () => {
      longPressCompleted.current = true
      try {
        await logOut()
        navigate('/')
      } catch (error) {
        console.error('Error logging out:', error)
      } finally {
        longPressCompleted.current = false
      }
    }, LONG_PRESS_DURATION)
  }

  const handleMouseEnter = () => {
    setShowTooltip(true)
  }

  const handleMouseLeave = () => {
    setShowTooltip(false)
  }

  const handleLongPressEnd = (e) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
    // Small delay to allow onClick to check longPressCompleted flag
    setTimeout(() => {
      setShowTooltip(false)
      longPressCompleted.current = false
    }, 50)
  }

  const handleLongPressCancel = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
    setShowTooltip(false)
    longPressCompleted.current = false
  }

  const handleExplicitLogout = async () => {
    try {
      await logOut()
      navigate('/')
    } catch (error) {
      console.error('Error logging out:', error)
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current)
      }
    }
  }, [])

  return (
    <motion.div
      className='digipalz-landing-page-navbar'
      initial={false}
      animate={{
        y: isVisible ? 0 : NAVBAR_HIDE_Y
      }}
      transition={{
        type: 'tween',
        ease: [0.4, 0, 0.2, 1],
        duration: 0.35
      }}
      style={{ pointerEvents: isVisible ? 'auto' : 'none' }}
    >
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
        className='digipalz-landing-navbar'
      >
        <div className='digipalz-landing-navbar__content'>
          <div className='digipalz-landing-navbar__left'>
            <Link
              to='/'
              className='digipalz-landing-navbar__logo-container'
              onClick={() => setMenuOpen(false)}
              style={{ cursor: 'pointer' }}
            >
              <h1
                className='digipalz-landing-navbar__logo-title text-3xl sm:text-4xl md:text-4xl lg:text-5xl text-black tracking-tight'
                style={{
                  fontFamily: 'Anton, sans-serif',
                  fontWeight: 400,
                  letterSpacing: '-0.5px'
                }}
              >
                Digipalz
              </h1>
            </Link>
          </div>

          <nav aria-label='Main navigation'>
            <ul
              className={`digipalz-landing-navbar__center ${
                (deviceType === 'mobile' || deviceType === 'tablet') && menuOpen
                  ? 'digipalz-landing-navbar__center--active'
                  : ''
              }`}
            >
              <li>
                <Link
                  to='/'
                  className={
                    isActive('/') ? 'digipalz-landing-navbar__link--active' : ''
                  }
                  onClick={() => setMenuOpen(false)}
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to='/about-us'
                  className={
                    isActive('/about-us')
                      ? 'digipalz-landing-navbar__link--active'
                      : ''
                  }
                  onClick={() => setMenuOpen(false)}
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to='/'
                  className={
                    isActive('/') ? 'digipalz-landing-navbar__link--active' : ''
                  }
                  onClick={handleHowItWorksClick}
                >
                  How It Works
                </Link>
              </li>

              <li>
                <Link
                  to='/educator-landing'
                  className={
                    isActive('/educator-landing')
                      ? 'digipalz-landing-navbar__link--active'
                      : ''
                  }
                  onClick={() => setMenuOpen(false)}
                >
                  Educators
                </Link>
              </li>
              <li>
                <Link
                  to='/pricing'
                  className={
                    isActive('/pricing')
                      ? 'digipalz-landing-navbar__link--active'
                      : ''
                  }
                  onClick={() => setMenuOpen(false)}
                >
                  Pricing
                </Link>
              </li>
              {/* <li>
            <a
              className={
                isActive('/contact-us')
                  ? 'digipalz-landing-navbar__link--active'
                  : ''
              }
              href='#'
              onClick={(e) => {
                e.preventDefault()
                navigate('/contact-us')
                setMenuOpen(false)
              }}
            >
              Contact Us
            </a>
          </li> */}
              {(deviceType === 'mobile' || deviceType === 'tablet') && (
                <li>
                  {currentUser ? (
                    <div
                      className='digipalz-landing-navbar__dashboard-wrapper'
                      style={{ position: 'relative' }}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      {showTooltip && (
                        <div className='digipalz-landing-navbar__tooltip'>
                          Tap for dashboard, Hold for logout
                        </div>
                      )}
                      <MotionLink
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                        className='digipalz-landing-navbar__mobile-trial-badge'
                        to={getDashboardPath()}
                        onClick={handleDashboardLinkClick}
                        onMouseDown={handleLongPressStart}
                        onMouseUp={handleLongPressEnd}
                        onMouseLeave={(e) => {
                          handleLongPressCancel()
                          handleMouseLeave()
                        }}
                        onTouchStart={handleLongPressStart}
                        onTouchEnd={handleLongPressEnd}
                        onTouchCancel={handleLongPressCancel}
                        aria-label='Go to dashboard. Long press to log out.'
                      >
                        Dashboard
                        <FaArrowRight aria-hidden='true' focusable='false' />
                      </MotionLink>
                      <button
                        type='button'
                        className='sr-only focus:not-sr-only focus:mt-2 focus:rounded-md focus:bg-black focus:px-3 focus:py-2 focus:text-white'
                        onClick={handleExplicitLogout}
                        aria-label='Log out'
                      >
                        Log out
                      </button>
                    </div>
                  ) : (
                    <Link
                      to='/login'
                      className='digipalz-landing-navbar__mobile-login'
                      onClick={() => setMenuOpen(false)}
                    >
                      Login
                    </Link>
                  )}
                </li>
              )}
            </ul>
          </nav>

          {/* Desktop: Show Login/Dashboard + Start Free Trial buttons */}
          {deviceType === 'desktop' && (
            <div className='digipalz-landing-navbar__right'>
              {currentUser ? (
                <div
                  className='digipalz-landing-navbar__dashboard-wrapper'
                  style={{ position: 'relative' }}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  {showTooltip && (
                    <div className='digipalz-landing-navbar__tooltip'>
                      Tap for dashboard, Hold for logout
                    </div>
                  )}
                  <MotionLink
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                    className='digipalz-landing-navbar__trial-btn'
                    onClick={handleDashboardLinkClick}
                    onMouseDown={handleLongPressStart}
                    onMouseUp={handleLongPressEnd}
                    onMouseLeave={(e) => {
                      handleLongPressCancel()
                      handleMouseLeave()
                    }}
                    onTouchStart={handleLongPressStart}
                    onTouchEnd={handleLongPressEnd}
                    onTouchCancel={handleLongPressCancel}
                    onContextMenu={(e) => {
                      // Prevent context menu on long press
                      e.preventDefault()
                    }}
                    to={getDashboardPath()}
                    aria-label='Go to dashboard. Long press to log out.'
                  >
                    Dashboard
                    <FaArrowRight aria-hidden='true' focusable='false' />
                  </MotionLink>
                  <button
                    type='button'
                    className='sr-only focus:not-sr-only focus:absolute focus:right-0 focus:top-full focus:mt-2 focus:rounded-md focus:bg-black focus:px-3 focus:py-2 focus:text-white'
                    onClick={handleExplicitLogout}
                    aria-label='Log out'
                  >
                    Log out
                  </button>
                </div>
              ) : (
                <Link
                  className='digipalz-landing-navbar__login-btn'
                  to='/login'
                >
                  Login
                </Link>
              )}
              {!currentUser && (
                <MotionLink
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className='digipalz-landing-navbar__trial-btn bg-black text-white px-6 sm:px-7 md:px-8 lg:px-10 py-3.5 md:py-4 rounded-full flex items-center gap-2.5 hover:bg-gray-900 transition-all shadow-lg text-base sm:text-lg lg:text-lg whitespace-nowrap'
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 700,
                    letterSpacing: '0.3px'
                  }}
                  onClick={handleRegisterClick}
                  to='/register'
                >
                  START FREE TRIAL
                  <FaArrowRight aria-hidden='true' focusable='false' />
                </MotionLink>
              )}
            </div>
          )}

          {/* Mobile & Tablet: FREE TRIAL button + hamburger menu */}
          {(deviceType === 'mobile' || deviceType === 'tablet') && (
            <div className='digipalz-landing-navbar__right digipalz-landing-navbar__right--mobile'>
              {!currentUser && (
                <MotionLink
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className='digipalz-landing-navbar__trial-btn'
                  to='/pricing'
                  onClick={() => setMenuOpen(false)}
                >
                  VIEW PLANS
                  <FaArrowRight aria-hidden='true' focusable='false' />
                </MotionLink>
              )}
              <button
                className='digipalz-landing-navbar__toggle'
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label='Toggle menu'
                type='button'
              >
                {menuOpen ? <FaTimes /> : <Menu />}
              </button>
            </div>
          )}
        </div>
      </motion.header>
    </motion.div>
  )
}

export default Navbar
